import httpHelper from '../utils/httpHelper';
import urlConstants from '../shared/urlConstants';
import { v4 as uuidv4 } from 'uuid';
import { Status } from '../components/DataGrid/MuiDataGrid';
import constants, { REPORT_TABLE } from '../shared/constants';
import { LastEvalKeyReportsRecord, LastEvaluatedKeyType } from '../redux-toolkit/reducers/lastEvaluatedKeyReducer';

type ReportRequest = {
	username: string;
	startDate: number;
	endDate: number;
}

const createCurrentUserReport = async (report: Status, username: string) => {

	const params = [
		{ param: '{object}', value: 'dailyreport' }
	];

	const bodyObj = {
		'organisationId': 'techpearl',
		'reportId': report.reportId || uuidv4(),
		'projectName': report.projectName,
		'activityType': report.activityType,
		'activityDuration': Number(report.activityDuration) || 0,
		'comments': report.comments,
		'workDescription': report.workDescription,
		'activityDate': Date.parse(report.activityDate),
		'userName': username,
		'feature': report.feature,
		'isource': report?.isource,
		'workMode': report?.workMode,
	};

	return httpHelper.postData(httpHelper.getUri(urlConstants.CREATE_ITEM, params), JSON.stringify(bodyObj));
};

export const USER_TYPE = ['EMPLOYEE', 'PROJECT_MANAGER', 'ADMIN', 'TEAM_LEAD'] as const;

type UserReportPostDataType = {
	fetchLimit: number | null;
	LastEvaluatedKey: LastEvalKeyReportsRecord | undefined;
	userName?: string;
	organisationId?: string;
	projectName?: string | null;
	displayProjectName?: string | null;
};

const getCurrentUserReports = async (
	userType: typeof USER_TYPE[number],
	{ username, lastEvaluatedKey }: { username: string, lastEvaluatedKey?: LastEvaluatedKeyType },
	{ onlyAdminReports = false, projectName = null }: { onlyAdminReports?: boolean, projectName?: string | null }
) => {

	const postData: UserReportPostDataType = {
		userName: '',
		LastEvaluatedKey: undefined,
		fetchLimit: null
	};

	if (userType === constants.EMPLOYEE_TYPES.EMPLOYEE || onlyAdminReports) {
		postData.userName = username;
	} else {
		postData.organisationId = constants.ORGANISATION_ID;
	}

	postData.LastEvaluatedKey = lastEvaluatedKey?.reports;
	postData.fetchLimit = REPORT_TABLE.PAGE_SIZE;
	postData.projectName = projectName ? projectName : null;

	const value = userType === constants.EMPLOYEE_TYPES.EMPLOYEE || onlyAdminReports
		? 'getUserReport'
		: 'getAllReport';

	const params = [
		{ param: '{actionName}', value }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const updateCurrentUserReport = async (report: Status) => {

	const activityDateTimestamp = typeof report.activityDate === 'number'
      ? report.activityDate
      : Date.parse(report.activityDate);

	const bodyObj = {
		'organisationId': 'techpearl',
		...report,
		'activityDate': activityDateTimestamp,
		'activityDuration': Number(report.activityDuration) || 0
	};

	const params = [
		{ param: '{object}', value: 'dailyreport' },
	];
	return httpHelper.postData(httpHelper.getUri(urlConstants.UPDATE_ITEM, params), JSON.stringify(bodyObj));
};

const deleteUserReport = (reportId: string) => {

	const bodyObj = {
		'organisationId': 'techpearl',
		reportId
	};

	const params = [
		{ param: '{object}', value: 'dailyreport' },
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.DELETE_ITEM, params), JSON.stringify(bodyObj));
};

const getAllReports = async (lastEvaluatedKey = null) => {

	const postData = {
		'organisationId': constants.ORGANISATION_ID,
		'LastEvaluatedKey': lastEvaluatedKey
	};

	const params = [
		{ param: '{actionName}', value: 'getAllReport' }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};






const getReportsFilteredByProjects = async (projectName: string, lastEvaluatedKey?: LastEvaluatedKeyType) => {

	const params = [
		{ param: '{actionName}', value: 'getProjectReport' },
	];

	const postData = {
		projectName,
		'organisationId': constants.ORGANISATION_ID,
		'LastEvaluatedKey': lastEvaluatedKey?.reports
	};

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getReportsFilteredByEmail = async (username: string, lastEvaluatedKey?: LastEvaluatedKeyType) => {

	const params = [
		{ param: '{actionName}', value: 'getUserReport' },
	];

	const postData = {
		userName: username,
		LastEvaluatedKey: lastEvaluatedKey?.reports,
		fetchLimit: REPORT_TABLE.PAGE_SIZE
	};

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getExceptionReports = async (startDate: string, endDate: string) => {

	const postData = {
		'organisationId': constants.ORGANISATION_ID,
		startDate: Date.parse(startDate),
		endDate: Date.parse(endDate)
	};

	const params = [
		{ param: '{actionName}', value: 'getExceptionReports' }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getAttendanceReports = async (startDate: string, endDate: string) => {

	const postData = {
		'organisationId': constants.ORGANISATION_ID,
		startDate: Date.parse(startDate),
		endDate: Date.parse(endDate)
	};

	const params = [
		{ param: '{actionName}', value: 'getAttendanceReport' }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getMonthlyReport = async (startDate: number, endDate: number) => {
	const postData = {
		'organisationId': constants.ORGANISATION_ID,
		startDate: startDate,
		endDate: endDate
	};

	const params = [
		{ param: '{actionName}', value: 'getMonthlyReport' }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getUserReportCount = async (username: string, userType: typeof USER_TYPE[number], onlyAdminReports = false) => {

	const postData: Record<string, string | null> = {
		LastEvaluatedKey: null
	};

	if (userType === constants.EMPLOYEE_TYPES.EMPLOYEE || onlyAdminReports) {

		postData.userName = username;
		postData.object = 'getUserReport';
	} else {

		postData.organisationId = 'techpearl';
		postData.object = 'getAllReport';
	}

	const params = [
		{ param: '{actionName}', value: 'getTotalItemCount' }
	];
	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getFilteredReportCount = async ({ filterType, filterValue, userName, onlyAdminReports }: { filterType: 'projectName' | 'email'; filterValue: string; userName?: string; onlyAdminReports?: boolean; }) => {

	const postData: Record<string, string> = {};

	if (filterType === 'projectName' && onlyAdminReports) {
		postData.projectName = filterValue;
		postData.object = 'getUserReport';
		postData.userName = userName ? userName : '';
	}
	else if (filterType === 'projectName') {

		postData.projectName = filterValue;
		postData.object = 'getProjectReport';
	} else if (filterType === 'email') {

		/*
		*	for filtering by email pass userName as filterValue
		*/
		postData.userName = filterValue;
		postData.object = 'getUserReport';
	} else {

		throw new Error('Invalid filter type selected!');
	}

	const params = [
		{ param: '{actionName}', value: 'getTotalItemCount' }
	];
	console.log(postData, 'postData');
	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const sendExceptionReportEmail = async (dailyreport: object) => {
	const postData: any = {};
	postData.dailyreport = dailyreport;
	const params = [
		{ param: '{actionName}', value: 'sendExceptionReport' }
	];
	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), postData);
};

const getMonthlySummaryException = async (username: string, startDate: any, endDate: any) => {
	const postData: any = {};
	postData.startDate = startDate;
	postData.endDate = endDate;
	postData.userName = username;
	const params = [
		{ param: '{actionName}', value: 'getMonthlySummaryReport' }
	];
	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

const getMonthlySummaryAllExceptions = async (requests: ReportRequest[]) => {
	const postData = requests.map(request => ({
		startDate: request.startDate,
		endDate: request.endDate,
		userName: request.username
	}));

	const params = [
		{ param: '{actionName}', value: 'getMonthlySummaryReport' }
	];

	const uri = httpHelper.getUri(urlConstants.ACTION, params);

	const apiCalls = postData.map(data => {
		return httpHelper.postData(uri, JSON.stringify(data));
	});

	return Promise.all(apiCalls);
};

const getUserReportData = async (firstDayEpoch: any, lastDayEpoch: any, username: any) => {
	const params = [
		{ param: '{actionName}', value: 'userReport' }
	];

	const postData = {
		'userName': username,
		'startDate': firstDayEpoch,
		'endDate': lastDayEpoch
	};

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};


const getProjectData = async (employeesWorking: any, weekStartEpoche: any, weekEndEpoche: any) => {

	const params = [
		{ param: '{actionName}', value: 'userReport' },
	];

	const postData = {
		userName: employeesWorking,
		startDate: weekStartEpoche,
		endDate: weekEndEpoche
	};

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};


const getMyTeamProjectData = async (organisationId: any, projectName: any, LastEvaluatedKey: any, limit: any) => {

	const postData: UserReportPostDataType = {
		userName: '',
		LastEvaluatedKey,
		fetchLimit: limit,
		organisationId:organisationId,
		displayProjectName:projectName,
		projectName
	};

	const params = [
		{ param: '{actionName}', value: 'getAllReport' }
	];

	return httpHelper.postData(httpHelper.getUri(urlConstants.ACTION, params), JSON.stringify(postData));
};

export {
	getMyTeamProjectData,
	getUserReportData,
	createCurrentUserReport,
	getCurrentUserReports,
	updateCurrentUserReport,
	deleteUserReport,
	getAllReports,
	getExceptionReports, getMonthlyReport, getAttendanceReports,
	getReportsFilteredByProjects,
	getReportsFilteredByEmail,
	getUserReportCount,
	getFilteredReportCount,
	sendExceptionReportEmail,
	getMonthlySummaryException,
	getProjectData,
	getMonthlySummaryAllExceptions,
};
